import React from 'react'

export interface ProfileMetaLineProps {
  memberYear?: number | string
  memberSinceLabel?: string
  showMemberSince?: boolean
  location?: string
  locationLabel?: string
  showLocation?: boolean
  birthday?: string
  birthdayLabel?: string
  showBirthday?: boolean
}

// Renders a translated label followed by its value, omitting the separating
// space when the label is empty (so an unresolved label doesn't leave an
// orphan leading space in front of the value).
function joinLabelValue (label: string, value: string): string {
  if (!label) return value
  if (!value) return label
  return `${label} ${value}`
}

function ProfileMetaLine ({
  memberYear,
  memberSinceLabel = '',
  showMemberSince = true,
  location,
  locationLabel = '',
  showLocation = false,
  birthday,
  birthdayLabel = '',
  showBirthday = false,
}: ProfileMetaLineProps) {
  const hasYear = showMemberSince && !!memberYear
  const hasLocation = showLocation && !!location
  const hasBirthday = showBirthday && !!birthday

  if (!hasYear && !hasLocation && !hasBirthday) return null

  let formattedBirthday = ''
  if (hasBirthday) {
    const date = new Date(`${birthday}T00:00`)
    formattedBirthday = Number.isNaN(date.getTime())
      ? ''
      : date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
  }

  // memberSinceLabel is a template containing the {year} placeholder. If the
  // caller passed an empty string (i.e. translation not resolved), skip the
  // template substitution and render nothing for that segment rather than
  // emitting the year on its own without context.
  const memberSinceText = hasYear && memberSinceLabel
    ? memberSinceLabel.replace('{year}', String(memberYear))
    : ''

  return (
    <span className="profile-meta-line">
      {memberSinceText ? (
        <span className="profile-meta-line__item">
          <span>{memberSinceText}</span>
        </span>
      ) : null}
      {hasLocation ? (
        <>
          {memberSinceText ? (
            <span className="profile-meta-line__dot" aria-hidden="true" />
          ) : null}
          <span className="profile-meta-line__item">
            <span>{joinLabelValue(locationLabel, location || '')}</span>
          </span>
        </>
      ) : null}
      {hasBirthday ? (
        <>
          {(memberSinceText || hasLocation) ? (
            <span className="profile-meta-line__dot" aria-hidden="true" />
          ) : null}
          <span className="profile-meta-line__item">
            <span>{joinLabelValue(birthdayLabel, formattedBirthday)}</span>
          </span>
        </>
      ) : null}
    </span>
  )
}

export default React.memo(ProfileMetaLine)
